@if (isForbidden) {
  <div class="modal-body">
    <app-not-authorized
      title="You cannot adjust stock"
      message="Manual stock adjustments are restricted to administrators."></app-not-authorized>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="onCancel()">Close</button>
  </div>
} @else {
  <form [formGroup]="adjustmentForm" (ngSubmit)="onSubmit()">
    <div class="modal-body p-4">
      <!-- What is being adjusted. Fixed: the form is always opened from a known row. -->
      <div class="adjustment-target mb-4">
        <div class="row g-3">
          <div class="col-md-6">
            <span class="form-label text-muted small mb-1 d-block">Material</span>
            <div class="fw-semibold">{{ context.raw_material_name }}</div>
            <div class="text-muted small font-monospace">Code: {{ context.raw_material_code }}</div>
          </div>
          <div class="col-md-6">
            <span class="form-label text-muted small mb-1 d-block">Warehouse</span>
            <div class="fw-semibold">{{ context.warehouse_name }}</div>
            @if (availableLabel) {
              <div class="text-muted small">
                Available now:
                <span class="fw-semibold">{{ availableLabel }}</span>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Direction. Deliberately the largest control on the form: sending an addition -->
      <!-- when a removal was meant is the expensive mistake here. -->
      <div class="mb-4">
        <label class="form-label">What is happening to the stock?</label>
        <div class="direction-toggle" role="group" aria-label="Adjustment direction">
          <button
            type="button"
            class="direction-option direction-add"
            [class.active]="!isRemoving"
            [attr.aria-pressed]="!isRemoving"
            (click)="setDirection(Direction.ADD)">
            <i class="bi bi-plus-circle-fill"></i>
            <span class="direction-title">Add stock</span>
            <span class="direction-hint">Goods on the shelf that the system is missing</span>
          </button>
          <button
            type="button"
            class="direction-option direction-remove"
            [class.active]="isRemoving"
            [attr.aria-pressed]="isRemoving"
            (click)="setDirection(Direction.REMOVE)">
            <i class="bi bi-dash-circle-fill"></i>
            <span class="direction-title">Remove stock</span>
            <span class="direction-hint">Goods the system counts that are not there</span>
          </button>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-md-6">
          <label for="adjustmentQuantity" class="form-label">
            Quantity
            <span class="text-danger">*</span>
          </label>
          <div class="input-group">
            <span class="input-group-text" [class.text-danger]="isRemoving" [class.text-success]="!isRemoving">
              {{ isRemoving ? '−' : '+' }}
            </span>
            <input
              id="adjustmentQuantity"
              type="number"
              class="form-control shadow-none"
              formControlName="quantity"
              placeholder="0"
              step="0.01"
              min="0"
              [class.is-invalid]="(quantityControl.invalid && quantityControl.touched) || !!quantityError"
              (input)="quantityError = null; shortageError = null" />
            <span class="input-group-text bg-light text-muted">{{ context.unit_short_name }}</span>
          </div>

          @if (quantityControl.touched && quantityControl.errors?.['required']) {
            <div class="text-danger small mt-1">Enter a quantity.</div>
          } @else if (quantityControl.touched && quantityControl.errors?.['quantityNotPositive']) {
            <div class="text-danger small mt-1">
              Enter a number above zero. Use the Add / Remove toggle to set the direction.
            </div>
          } @else if (quantityControl.touched && quantityControl.errors?.['quantityInvalid']) {
            <div class="text-danger small mt-1">Enter a valid number.</div>
          } @else if (quantityError) {
            <div class="text-danger small mt-1">{{ quantityError }}</div>
          }

          <!-- The shortage response, rendered against the field that caused it. -->
          @if (shortageError) {
            <div class="alert alert-danger py-2 px-3 small mt-2 mb-0">
              <div class="fw-semibold mb-1">{{ shortageError.quantity }}</div>
              <div class="d-flex flex-wrap gap-3">
                <span>
                  Available:
                  <strong>{{ formatQuantity(shortageError.available) }} {{ context.unit_short_name }}</strong>
                </span>
                <span>
                  Requested:
                  <strong>{{ formatQuantity(shortageError.requested) }} {{ context.unit_short_name }}</strong>
                </span>
                <span>
                  Short by:
                  <strong>{{ formatQuantity(shortageError.shortage) }} {{ context.unit_short_name }}</strong>
                </span>
              </div>
            </div>
          }
        </div>

        <div class="col-md-6">
          <label class="form-label">
            Reason
            <span class="text-danger">*</span>
          </label>
          <ng-select
            placeholder="Select a reason"
            [items]="reasonOptions"
            formControlName="reason"
            bindValue="value"
            bindLabel="label"
            [clearable]="false"
            [searchable]="false" />
          @if (reasonControl.touched && reasonControl.invalid) {
            <div class="text-danger small mt-1">Pick the reason for this adjustment.</div>
          }
        </div>

        <div class="col-12">
          <label for="adjustmentRemarks" class="form-label">
            Remarks
            @if (isRemarksRequired) {
              <span class="text-danger">*</span>
            } @else {
              <span class="text-muted small fw-normal">(optional)</span>
            }
          </label>
          <textarea
            id="adjustmentRemarks"
            class="form-control shadow-none"
            formControlName="remarks"
            rows="2"
            placeholder="What happened, in one line"
            [class.is-invalid]="(remarksControl.invalid && remarksControl.touched) || !!remarksError"
            (input)="remarksError = null"></textarea>
          @if (remarksControl.touched && remarksControl.errors?.['required']) {
            <div class="text-danger small mt-1">Remarks are required when the reason is "Other".</div>
          } @else if (remarksError) {
            <div class="text-danger small mt-1">{{ remarksError }}</div>
          }
        </div>
      </div>

      @if (previewLabel) {
        <div
          class="alert py-2 px-3 small mt-3 mb-0"
          [class.alert-danger]="isRemoving"
          [class.alert-success]="!isRemoving">
          <i class="bi bi-arrow-right-circle me-1"></i>
          {{ previewLabel }}
        </div>
      }

      @if (submitError) {
        <app-error-alert class="d-block mt-3" [error]="submitError"></app-error-alert>
      }
    </div>

    <div class="modal-footer p-4 pt-0 border-0">
      <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="isSubmitting">Cancel</button>
      <button
        type="submit"
        class="btn"
        [class.btn-danger]="isRemoving"
        [class.btn-primary]="!isRemoving"
        [disabled]="isSubmitting">
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm me-1"></span>
          Saving...
        } @else {
          <i class="bi bi-check-circle me-1"></i>
          {{ isRemoving ? 'Remove stock' : 'Add stock' }}
        }
      </button>
    </div>
  </form>
}
